import { derived, type Readable } from 'svelte/store';
import {
  type CreateMutateFunction,
  createMutation,
  createQuery,
  type QueryStatus,
} from '$lib/api/tanstack-query';

import type * as APITypes from '@viamrobotics/web-client/app/v1/app';

import { assertExists } from '$lib/assert';
import { AuthStatus, useAuthContext } from '$lib/auth';
import type { Permission, ResourceType, Role } from '$lib/permissions';
import { IdentityType } from '$lib/permissions';

import { createListKeysQuery } from '../api-keys';
import { useAPIClient } from '../client';
import { useQueryInvalidator } from '../invalidate';
import { createMutationOptions } from '../mutation-options';
import { createQueryOptions } from '../query-options';
import { useUserContext } from '../user';
import {
  createHasPermissionsQueries,
  createHasPermissionsQuery,
  type CreatePermissionsQueryRequest,
} from './has-permissions';
import {
  getInvalidatedQueriesForRoleMutations,
  permissionKeys,
} from './query-key';

export { prefetchHasPermissions } from './has-permissions';
export { permissionKeys } from './query-key';

const ASSERT_USER_ID_EXISTS_MESSAGE = 'Missing user ID';

/** Request interface for a call to UsePermissions */
export interface UsePermissionsRequest extends CreatePermissionsQueryRequest {
  match: Permission | undefined;
}

/** Given a resource and data from the API, derive resource permissions state. */
export const hasPermission = (
  permission: Permission | undefined,
  resourceType: ResourceType | undefined,
  resourceId: string | undefined,
  checkPermissionsResponse: APITypes.CheckPermissionsResponse | undefined
): boolean => {
  return permission && resourceType && resourceId
    ? Boolean(
        checkPermissionsResponse?.authorizedPermissions.some(
          (perm) =>
            (perm.resourceType as ResourceType) === resourceType &&
            perm.resourceId === resourceId &&
            perm.permissions.includes(permission)
        )
      )
    : false;
};

/** Function signature for checking permissions on a resource */
export type UsePermissions = (
  request: Readable<UsePermissionsRequest>
) => Readable<boolean>;

interface PermissionsState {
  queryStatus: QueryStatus;
  hasPermission: boolean;
}

/**
 * Check if the user has the permissions on the resource
 *
 * @param match the permissions to check.
 * @param resourceType the type of the resource
 * @param resourceId the ID of the resource
 * @returns a permissions state readable store containing the query status
 * and whether the user has permissions.
 */
export const usePermissionsWithQueryStatus = (
  request: Readable<UsePermissionsRequest>
): Readable<PermissionsState> => {
  const hasPermissionQuery = createHasPermissionsQuery(request);

  return derived([hasPermissionQuery, request], ([$query, $request]) => ({
    queryStatus: $query.status,
    hasPermission: hasPermission(
      $request.match,
      $request.resourceType,
      $request.resourceID,
      $query.data
    ),
  }));
};

/**
 * Check many permissions at the same time. Use only if you need to query for permissions across multiple organizations.
 * Returns permission results, indexed by resource ID.
 */
export const useManyPermissions = (
  requests: Readable<UsePermissionsRequest[]>
): Readable<Record<string, boolean>> => {
  const hasPermissionQueries = createHasPermissionsQueries(requests);
  return derived(
    [hasPermissionQueries, requests],
    ([$queries, $requests]) =>
      Object.fromEntries(
        $requests.map(($request, idx) => [
          $request.resourceID,
          hasPermission(
            $request.match,
            $request.resourceType,
            $request.resourceID,
            $queries[idx]?.data
          ),
        ])
      ) as Record<string, boolean>
  );
};

/**
 * Check if the user has the permissions on the resource
 *
 * @param match the permissions to check.
 * @param resourceType the type of the resource
 * @param resourceId the ID of the resource
 * @returns a boolean readable store.
 */
export const usePermissions = (
  request: Readable<UsePermissionsRequest>
): Readable<boolean> => {
  const hasPermissionQuery = createHasPermissionsQuery(request);

  return derived([hasPermissionQuery, request], ([$query, $request]) =>
    hasPermission(
      $request.match,
      $request.resourceType,
      $request.resourceID,
      $query.data
    )
  );
};

/** Data from an RPC API call to CheckPermissions. */
export interface ListAuthorizationsQuery {
  data: APITypes.ListAuthorizationsResponse | undefined;
}

const rolePriorities: Record<Role, number> = {
  organization_owner: 1,
  organization_operator: 2,
  location_owner: 3,
  location_operator: 4,
  robot_owner: 5,
  robot_operator: 6,
} as const;

/**
 * Returns the priority ranking of a role.
 * @returns 1-6 on a valid role, where 1 represents the highest priority and 6 is the lowest. 0 on undefined.
 */
export const getRolePriority = (id: Role | undefined): number =>
  id === undefined ? 0 : rolePriorities[id];

/** Given data from the API, build map from user ID to authorization. */
export const buildIdentityIDToAuthorizationsMap = (
  userAuthorizations: APITypes.Authorization[] = [],
  apiKeyAuthorizations: APITypes.APIKeyWithAuthorizations[] = []
): Record<string, APITypes.Authorization[]> => {
  const authsByIdentityId: Record<string, APITypes.Authorization[]> = {};

  for (const auth of userAuthorizations) {
    const auths = authsByIdentityId[auth.identityId] ?? [];
    auths.push(auth);
    authsByIdentityId[auth.identityId] = auths;
  }

  for (const key of apiKeyAuthorizations) {
    if (!key.apiKey?.id) {
      continue;
    }
    const keyId = key.apiKey.id;
    for (const auth of key.authorizations) {
      const idAuths = authsByIdentityId[keyId] ?? [];
      idAuths.push({
        authorizationType: auth.authorizationType,
        authorizationId: auth.authorizationId,
        resourceType: auth.resourceType,
        resourceId: auth.resourceId,
        identityId: keyId,
        organizationId: auth.orgId,
        identityType: IdentityType.API_KEY,
      });
      authsByIdentityId[keyId] = idAuths;
    }
  }

  // sort the auth arrays by role priority
  for (const auths of Object.values(authsByIdentityId)) {
    auths.sort(
      (a, b) =>
        getRolePriority(a.authorizationId as Role) -
        getRolePriority(b.authorizationId as Role)
    );
  }
  return authsByIdentityId;
};

/** Given data from the API, build map from resource ID to authorization. */
export const buildResourceIDToAuthorizationsMap = (
  userAuthorizations: APITypes.Authorization[] = [],
  apiKeyAuthorizations: APITypes.APIKeyWithAuthorizations[] = []
): Record<string, APITypes.Authorization[]> => {
  const authsByResourceID: Record<string, APITypes.Authorization[]> = {};

  for (const auth of userAuthorizations) {
    const resourceAuths = authsByResourceID[auth.resourceId] ?? [];
    resourceAuths.push(auth);
    authsByResourceID[auth.resourceId] = resourceAuths;
  }

  for (const key of apiKeyAuthorizations) {
    if (!key.apiKey?.id) {
      continue;
    }
    const keyId = key.apiKey.id;
    for (const auth of key.authorizations) {
      const resourceAuths = authsByResourceID[auth.resourceId] ?? [];
      resourceAuths.push({
        authorizationType: auth.authorizationType,
        authorizationId: auth.authorizationId,
        resourceType: auth.resourceType,
        resourceId: auth.resourceId,
        identityId: keyId,
        organizationId: auth.orgId,
        identityType: IdentityType.API_KEY,
      });
      authsByResourceID[auth.resourceId] = resourceAuths;
    }
  }
  return authsByResourceID;
};

/**
 * List all authorizations by user ID in an organization
 *
 * @param organizationID the organization to query
 * @returns a readable store mapping user IDs to authorizations
 */
export const useAuthsByIdentityIDMap = (
  organizationID: Readable<string | undefined>
): Readable<Record<string, APITypes.Authorization[]>> => {
  const listAuthorizationsQuery = createListAuthorizationsQuery(organizationID);
  const listKeysQuery = createListKeysQuery(organizationID);

  return derived(
    [listAuthorizationsQuery, listKeysQuery],
    ([userAuths, apiKeyAuths]) =>
      buildIdentityIDToAuthorizationsMap(
        userAuths.data?.authorizations,
        apiKeyAuths.data
      )
  );
};

/**
 * List all authorizations by resource ID in an organization
 *
 * @param organizationID the organization to query
 * @returns a readable store mapping resource IDs to authorizations
 */
export const useAuthsByResourceIDMap = (
  organizationID: Readable<string | undefined>
): Readable<Record<string, APITypes.Authorization[]>> => {
  const listAuthorizationsQuery = createListAuthorizationsQuery(organizationID);
  const listKeysQuery = createListKeysQuery(organizationID);

  return derived(
    [listAuthorizationsQuery, listKeysQuery],
    ([userAuths, apiKeyAuths]) =>
      buildResourceIDToAuthorizationsMap(
        userAuths.data?.authorizations,
        apiKeyAuths.data
      )
  );
};

const createListAuthorizationsQuery = (
  organizationID: Readable<string | undefined>
) => {
  const apiClient = useAPIClient();
  const { authState, getRPCOptions } = useAuthContext();

  const queryOptions = derived(
    [authState, organizationID],
    ([$authState, $organizationID]) =>
      createQueryOptions({
        queryKey: permissionKeys.authorizations($organizationID),
        enabled:
          $authState.status === AuthStatus.LoggedIn &&
          $organizationID !== undefined,
        queryFn: async () => {
          assertExists($organizationID, 'Missing org ID');

          const rpcOptions = await getRPCOptions();
          const { response } = await apiClient.listAuthorizations(
            {
              organizationId: $organizationID,
              resourceIds: [],
            },
            rpcOptions
          );

          return response;
        },
      })
  );

  return createQuery(queryOptions);
};

/** A mutator function to create a store */
export type AddRole = CreateMutateFunction<
  APITypes.AddRoleResponse,
  Error,
  APITypes.AddRoleRequest
>;

export const createAddRoleMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { userID } = useUserContext();
  const { invalidateQueries } = useQueryInvalidator();

  const mutationOptions = derived(userID, ($userID) =>
    createMutationOptions({
      mutationFn: async (request: APITypes.AddRoleRequest) => {
        const rpcOptions = await getRPCOptions();
        const { response } = await apiClient.addRole(request, rpcOptions);
        return response;
      },
      onSuccess: async (_data, request) => {
        assertExists($userID, ASSERT_USER_ID_EXISTS_MESSAGE);
        assertExists(
          request.authorization,
          'authorization to add is undefined'
        );

        await invalidateQueries(
          getInvalidatedQueriesForRoleMutations(
            request.authorization.identityId,
            request.authorization.organizationId,
            request.authorization.resourceId,
            $userID
          )
        );
      },
    })
  );

  return createMutation(mutationOptions);
};

/** A mutator function to create a store */
export type RemoveRole = CreateMutateFunction<
  APITypes.RemoveRoleResponse,
  Error,
  APITypes.RemoveRoleRequest
>;

/** Used to remove an authorization (role) from an identity */
export const useRemoveRole = (): Readable<RemoveRole> => {
  const mutation = createRemoveRoleMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

const createRemoveRoleMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { userID } = useUserContext();
  const { invalidateQueries } = useQueryInvalidator();

  const mutationOptions = derived(userID, ($userID) =>
    createMutationOptions({
      mutationFn: async (request: APITypes.RemoveRoleRequest) => {
        const rpcOptions = await getRPCOptions();
        const { response } = await apiClient.removeRole(request, rpcOptions);
        return response;
      },
      onSuccess: async (_data, request) => {
        assertExists($userID, ASSERT_USER_ID_EXISTS_MESSAGE);
        assertExists(
          request.authorization,
          'authorization to remove is undefined'
        );

        await invalidateQueries(
          getInvalidatedQueriesForRoleMutations(
            request.authorization.identityId,
            request.authorization.organizationId,
            request.authorization.resourceId,
            $userID
          )
        );
      },
    })
  );

  return createMutation(mutationOptions);
};

/** A mutator function to create a store */
export type ChangeRole = CreateMutateFunction<
  APITypes.ChangeRoleResponse,
  Error,
  APITypes.ChangeRoleRequest
>;

/** Used to change an authorization (role) on an identity to another */
export const useChangeRole = (): Readable<ChangeRole> => {
  const mutation = createChangeRoleMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

export const createChangeRoleMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { userID } = useUserContext();
  const { invalidateQueries } = useQueryInvalidator();

  const mutationOptions = derived(userID, ($userID) =>
    createMutationOptions({
      mutationFn: async (request: APITypes.ChangeRoleRequest) => {
        const rpcOptions = await getRPCOptions();
        const { response } = await apiClient.changeRole(request, rpcOptions);
        return response;
      },
      onSuccess: async (_data, request) => {
        assertExists($userID, ASSERT_USER_ID_EXISTS_MESSAGE);

        assertExists(
          request.oldAuthorization,
          'authorization to change is undefined'
        );

        /*
         * Only support changing role for the same identity within the same org
         */
        await invalidateQueries(
          getInvalidatedQueriesForRoleMutations(
            request.oldAuthorization.identityId,
            request.oldAuthorization.organizationId,
            request.oldAuthorization.resourceId,
            $userID
          )
        );
      },
    })
  );

  return createMutation(mutationOptions);
};
